import React, { useState, useEffect, useRef, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';

// ✅ DYNAMIC STYLES FOR REACT SELECT (compact)
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '28px',
    height: '28px',
    fontSize: '12px',
    borderRadius: '4px',
    borderColor: state.isFocused ? 'var(--theme-color, #0088cc)' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 2px color-mix(in srgb, var(--theme-color, #0088cc), transparent 85%)' : 'none',
    '&:hover': { borderColor: 'var(--theme-color, #0088cc)' },
    borderWidth: '1px'
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '2px' }),
  clearIndicator: (base) => ({ ...base, padding: '2px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({ ...base, zIndex: 9999, borderRadius: '6px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    padding: '5px 10px',
    backgroundColor: state.isSelected
      ? 'var(--theme-color, #0088cc)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--theme-color, #0088cc), white 90%)'
        : 'white',
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: 'var(--theme-color, #0088cc)', color: 'white' }
  }),
  placeholder: (base) => ({ ...base, color: '#6c757d', fontSize: '12px' })
};

function initItem() {
  return {
    medID: null, medicineName: "", category: "", companyName: "", packSize: "",
    unit: "", medicinecomposition: "", entryDate: new Date().toISOString().slice(0, 10),
    mrp: "", qty: "", freeQty: "", totQtyInPcs: 0, pRate: "", gstP: "", hsnCode: "",
    mfgDate: "", expDate: "", batchNo: "", inScheme: "", piece: "", leaf: "",
    freeLeaf: "", freePiece: "", sRate: "", saleDis: "", dis: "", cd: "", cdInDis: "",
    taxable: 0, gstAmt: 0, totals: 0,
  };
}

const round2 = (v) => (Number.isFinite(v) ? parseFloat(v.toFixed(2)) : 0);
const getToken = () => sessionStorage.getItem("token") || "";

// ── Validation helpers ──────────────────────────────────────────────
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
const isNumeric = (v) => !isBlank(v) && !isNaN(Number(v)) && Number.isFinite(Number(v));
const isValidDate = (d) => !isBlank(d) && !isNaN(new Date(d).getTime());

const MedPurchase = () => {
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(initItem);
  const [billDate, setBillDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [billNo, setBillNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [transport, setTransport] = useState("");
  const [headerCD, setHeaderCD] = useState(0);
  const [totals, setTotals] = useState({ totalTaxable: 0, totalGst: 0, totalAmount: 0 });
  const [frid] = useState(1);
  const [hid] = useState(1);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ NEW: per-field validation errors
  const [itemErrors, setItemErrors] = useState({});   // current medicine row
  const [headerErrors, setHeaderErrors] = useState({}); // vendor / bill

  const formRef = useRef(null);
  const medicineSelectRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      const firstInput = formRef.current?.querySelector('select, input:not([type="hidden"]), .react-select__input input');
      if (firstInput) firstInput.focus();
    }, 300);
  }, []);

  // ─────────────────────────────────────────────────────────────
  // KEYBOARD NAVIGATION (unchanged — Enter moves to next field)
  // ─────────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    const isInsideAsyncSelect = e.target.closest('.react-select__control') || e.target.classList?.contains('react-select__input');
    if (isInsideAsyncSelect && e.key === 'Enter') return;
    const isReactSelectMenuOpen = document.querySelector('.react-select__menu');
    if (isReactSelectMenuOpen) return;

    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]):not([tabindex="-1"]), ' +
      '.react-select__input input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([readonly]), ' +
      'button:not([disabled]):not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);
    const isAddButton = e.target.textContent?.includes("Add Medicine") || e.target.closest('button')?.textContent?.includes("Add Medicine");

    if (e.key === "Tab") return;

    if (e.key === "Enter") {
      if (isAddButton) { e.preventDefault(); handleAddItem(); return; }
      if (currentIndex === focusableElements.length - 1) { e.preventDefault(); e.target.closest("form").requestSubmit(); return; }
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    }
  };

  useEffect(() => {
    const handler = (e) => { if (formRef.current && e.target.closest("form") === formRef.current) handleKeyDown(e); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [handleKeyDown]);

  // ─────────────────────────────────────────────────────────────
  // DATA LOADERS (unchanged)
  // ─────────────────────────────────────────────────────────────
  const loadVendorOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
      const res = await customFetch(`/VendorDT/search?name=${encodeURIComponent(inputValue)}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((v) => ({ value: v.vid, label: `${v.vendorName} (${v.gstin || "No GST"})`, data: v }));
    } catch { return []; }
  };

  const handleVendorChange = (selected) => {
    setSelectedVendor(selected ? selected.data : null);
    setHeaderErrors((prev) => ({ ...prev, vendor: undefined }));
  };

  const loadMedicineOptions = async (inputValue) => {
    if (!inputValue) return [];
    try {
      const res = await customFetch(`/MedicineDT/search?medicine=${encodeURIComponent(inputValue)}`);
      if (!res.ok) return [];
      const data = await res.json();
      return data.map((m) => ({ value: m.medID, label: m.medicine, data: m }));
    } catch { return []; }
  };

  const handleMedicineChange = (selected) => {
    if (!selected) { setCurrentItem(initItem()); setItemErrors({}); return; }
    const m = selected.data;
    setCurrentItem((prev) => ({
      ...prev, medID: m.medID, medicinecomposition: m.mediInUnit, medicineName: m.medicine,
      category: m.categoryName, companyName: m.brandName, packSize: m.iUnit, unit: m.mUnit, hsnCode: m.hsncode || "",
      gstP: m.gstp || m.gstP || 0,
      mrp: "", sRate: "",
    }));
    setItemErrors({});
    setError(""); setSuccess("");
  };

  const fetchBatchDetails = async (batchNo) => {
    if (!currentItem.medID || !batchNo || !batchNo.trim()) return;
    try {
      const res = await customFetch(
        `/MedPurchase/last-batch-details?medId=${currentItem.medID}&batchNo=${encodeURIComponent(batchNo.trim())}`
      );
      if (res.ok) {
        const data = await res.json();
        setCurrentItem(prev => {
          // Batch wala HSN blank nahi hai toh overwrite karein, warna purana rakhein
          const finalHSN = (data.hsncode && data.hsncode.trim() !== "")
            ? data.hsncode
            : prev.hsnCode;
          const updated = {
            ...prev,
            hsnCode: finalHSN,
            mfgDate: data?.mfgDate || "",
            expDate: data?.expDate || "",
            pRate: data?.pRate || "",
            dis: data?.dis1 || data?.diS1 || "",
            gstP: data?.gstp || "",
            mrp: data?.mrp || "",
            saleDis: data?.saleDis || "",
            sRate: data?.sRate || ""
          };
          return recalcItem(updated);
        });
        setSuccess(`Batch details loaded.`);
        setError("");
      } else if (res.status === 404) {
        console.log("New batch entry.");
      }
    } catch (err) {
      console.error("Batch fetch error:", err);
      setError("Error fetching batch history.");
    }
  };

  // ─────────────────────────────────────────────────────────────
  // CALCULATION ENGINE (unchanged — do NOT touch)
  // ─────────────────────────────────────────────────────────────
  const recalcItem = (item) => {
    if (!item) return initItem();
    const packSize = parseFloat(item.packSize) || 1;
    const leaf = parseFloat(item.leaf) || 0;
    const piece = parseFloat(item.piece) || 0;
    const freeLeaf = parseFloat(item.freeLeaf) || 0;
    const freePiece = parseFloat(item.freePiece) || 0;

    const paidPcs = leaf * packSize + piece;
    const freePcs = freeLeaf * packSize + freePiece;
    const totalPcs = paidPcs + freePcs;

    const pRateLeaf = parseFloat(item.pRate) || 0;
    const gstPercent = parseFloat(item.gstP) || 0;
    const disPercent = parseFloat(item.dis) || 0;

    const pieceRate = packSize > 0 ? pRateLeaf / packSize : 0;
    const grossAmount = paidPcs * pieceRate;
    const disAmount = (grossAmount * disPercent) / 100;
    const taxable = grossAmount - disAmount;
    const gstAmt = (taxable * gstPercent) / 100;
    const lineTotal = taxable + gstAmt;

    return {
      ...item,
      totQtyInPcs: round2(paidPcs),
      freeQty: round2(freePcs),
      qty: round2(totalPcs),
      taxable: round2(taxable),
      gstAmt: round2(gstAmt),
      totals: round2(lineTotal)
    };
  };

  const applyItemChange = (item, name, value) => {
    let newItem = { ...item, [name]: value };

    if (name === "mrp" || name === "saleDis") {
      const mrp = parseFloat(newItem.mrp) || 0;
      const saleDis = parseFloat(newItem.saleDis) || 0;
      if (mrp > 0) newItem.sRate = round2(mrp * (1 - saleDis / 100)).toString();
    }

    if (name === "sRate") {
      const mrp = parseFloat(newItem.mrp) || 0;
      const sRate = parseFloat(newItem.sRate) || 0;
      if (mrp > 0) newItem.saleDis = round2(((mrp - sRate) / mrp) * 100).toString();
    }

    return recalcItem(newItem);
  };

  const handleItemChange = (e) => {
    const { name } = e.target;
    setCurrentItem(applyItemChange(currentItem, name, e.target.value));
    // live: jis field mein type kar rahe ho uska error hata do
    if (itemErrors[name]) setItemErrors((prev) => ({ ...prev, [name]: undefined }));
    if (name === "leaf" || name === "piece") setItemErrors((prev) => ({ ...prev, qty: undefined }));
  };

  // ─────────────────────────────────────────────────────────────
  // VALIDATION (per-field, runs BEFORE save → no backend surprises)
  // ─────────────────────────────────────────────────────────────
  const validateCurrentItem = (it) => {
    const e = {};
    if (!it.medID) e.medicineName = "Select a medicine";
    if (isBlank(it.batchNo)) e.batchNo = "Batch required";
    if (isBlank(it.hsnCode)) e.hsnCode = "HSN required";

    if (isBlank(it.mfgDate)) e.mfgDate = "Mfg required";
    else if (!isValidDate(it.mfgDate)) e.mfgDate = "Invalid date";

    if (isBlank(it.expDate)) e.expDate = "Exp required";
    else if (!isValidDate(it.expDate)) e.expDate = "Invalid date";
    else if (isValidDate(it.mfgDate) && new Date(it.expDate) < new Date(it.mfgDate)) e.expDate = "Exp < Mfg";

    // qty fields must be whole non-negative numbers
    [["leaf", "Leaf"], ["piece", "Piece"], ["freeLeaf", "Free Leaf"], ["freePiece", "Free Pc"]].forEach(([f]) => {
      if (!isBlank(it[f]) && (!isNumeric(it[f]) || Number(it[f]) < 0 || !Number.isInteger(Number(it[f])))) e[f] = "Whole no.";
    });
    if (!it.qty || Number(it.qty) <= 0) e.qty = "Enter Leaf/Piece (qty > 0)";

    // rates / amounts
    if (!isNumeric(it.pRate) || Number(it.pRate) <= 0) e.pRate = "Rate > 0";
    if (!isNumeric(it.mrp) || Number(it.mrp) <= 0) e.mrp = "MRP > 0";
    if (!isBlank(it.dis) && (!isNumeric(it.dis) || Number(it.dis) < 0 || Number(it.dis) > 100)) e.dis = "0–100";
    if (!isBlank(it.saleDis) && (!isNumeric(it.saleDis) || Number(it.saleDis) < 0 || Number(it.saleDis) > 100)) e.saleDis = "0–100";
    if (!isBlank(it.sRate) && !isNumeric(it.sRate)) e.sRate = "Number only";
    return e;
  };

  const validateHeader = () => {
    const e = {};
    if (!selectedVendor) e.vendor = "Select a vendor";
    if (isBlank(billNo)) e.billNo = "Bill No required";
    if (isBlank(billDate) || !isValidDate(billDate)) e.billDate = "Valid bill date required";
    if (!isBlank(headerCD) && !isNumeric(headerCD)) e.headerCD = "Number only";
    return e;
  };

  const handleAddItem = () => {
    const errs = validateCurrentItem(currentItem);
    setItemErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError("Please fix the highlighted fields before adding the medicine.");
      // focus first invalid field
      setTimeout(() => {
        const el = formRef.current?.querySelector('.is-invalid');
        if (el && el.focus) el.focus();
      }, 50);
      return;
    }
    setError(""); setSuccess("");
    setItems((prev) => [currentItem, ...prev]);
    setCurrentItem(initItem());
    setItemErrors({});
    setTimeout(() => { if (medicineSelectRef.current) medicineSelectRef.current.focus(); }, 150);
  };

  const handleRemoveItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleEditRowToForm = (index) => {
    const row = items[index];
    if (!row) return;
    const itemToEdit = recalcItem({ ...row });
    setCurrentItem(itemToEdit);
    setItems((prev) => prev.filter((_, i) => i !== index));
    setItemErrors({});
    setError(""); setSuccess("");
  };

  useEffect(() => {
    const totalTaxable = items.reduce((sum, it) => sum + (parseFloat(it.taxable) || 0), 0);
    const totalGst = items.reduce((sum, it) => sum + (parseFloat(it.gstAmt) || 0), 0);
    const totalAmount = items.reduce((sum, it) => sum + (parseFloat(it.totals) || 0), 0);
    setTotals({ totalTaxable: round2(totalTaxable), totalGst: round2(totalGst), totalAmount: round2(totalAmount) });
  }, [items]);

  // ─────────────────────────────────────────────────────────────
  // SAVE (payload mapping unchanged — only added header validation)
  // ─────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();

    const hErrs = validateHeader();
    setHeaderErrors(hErrs);
    if (Object.keys(hErrs).length > 0) { setError("Please fix the highlighted header fields."); return; }
    if (items.length === 0) { setError("At least add one medicine."); return; }

    setError(""); setSuccess(""); setLoading(true);
    try {
      const token = getToken();
      const payload = {
        frid, hid, vendorId: selectedVendor.vid, billDate, billNo, remarks, transport, entryBy: null,
        cd: parseFloat(headerCD) || 0,
        items: items.map((it) => ({
          medID: it.medID, entryDate: it.entryDate, qty: parseFloat(it.qty) || 0, freeQty: parseFloat(it.freeQty) || 0,
          totQtyInPcs: parseFloat(it.totQtyInPcs) || 0, pRate: parseFloat(it.pRate) || 0, gstP: parseFloat(it.gstP) || 0,
          taxable: parseFloat(it.taxable) || 0, gstAmt: parseFloat(it.gstAmt) || 0, totals: parseFloat(it.totals) || 0,
          mrp: parseFloat(it.mrp) || 0, sRate: parseFloat(it.sRate) || 0, hsnCode: it.hsnCode || "", batchNo: it.batchNo || "",
          mfgDate: it.mfgDate || null, expDate: it.expDate || null, inScheme: parseFloat(it.inScheme) || 0,
          piece: it.piece ? parseInt(it.piece) : 0, leaf: it.leaf ? parseInt(it.leaf) : 0,
          freeLeaf: it.freeLeaf ? parseInt(it.freeLeaf) : 0, freePiece: it.freePiece ? parseInt(it.freePiece) : 0,
          dis: parseFloat(it.dis) || 0, saleDis: parseFloat(it.saleDis) || 0, cd: parseFloat(it.cd) || 0, cdInDis: parseFloat(it.cdInDis) || 0,
        })),
      };
      const res = await customFetch(`/MedPurchase/save`, {
        method: "POST",
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error((await res.text()) || "Failed to save purchase.");
      const data = await res.json();
      setSuccess(`Purchase saved successfully. Bill No: ${data.billNo}`);
      setItems([]); setCurrentItem(initItem()); setRemarks(""); setTransport(""); setHeaderCD(0); setBillNo(""); setSelectedVendor(null);
      setItemErrors({}); setHeaderErrors({});
    } catch (err) { setError(err.message || "Error occurred"); } finally { setLoading(false); }
  };

  // small helpers for rendering field errors
  const cls = (base, f) => `${base} ${itemErrors[f] ? "is-invalid" : ""}`;
  const Err = ({ f }) => (itemErrors[f] ? <div className="mp-err">{itemErrors[f]}</div> : null);

  return (
    <div className="container-fluid mt-0 mp-compact">
      <div className="card shadow-sm border-0">
        <div className="card-header py-2">
          <h6 className="mb-0 fw-bold text-white"><i className="bi bi-cart-plus me-2"></i>Medicine Purchase</h6>
        </div>

        <div className="card-body fs-7 p-2">
          {error && <div className="alert-custom alert-danger py-1 mb-2">{error}</div>}
          {success && <div className="alert-custom alert-success py-1 mb-2">{success}</div>}

          <form ref={formRef} onSubmit={handleSave} noValidate>
            {/* ── Vendor / Bill Section ── */}
            <div className="border p-2 mb-2 rounded bg-light section-box">
              <div className="row g-2 align-items-start">
                <div className="col-md-3">
                  <label className="form-label fw-bold text-primary">Search Vendor <span className="text-danger">*</span></label>
                  <AsyncSelect
                    cacheOptions loadOptions={loadVendorOptions} onChange={handleVendorChange} placeholder="Search vendor..." isClearable
                    styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    className={headerErrors.vendor ? "rs-invalid" : ""}
                    value={selectedVendor ? { value: selectedVendor.vid, label: `${selectedVendor.vendorName} (${selectedVendor.gstin || "No GST"})` } : null}
                  />
                  {headerErrors.vendor && <div className="mp-err">{headerErrors.vendor}</div>}
                </div>
                {selectedVendor && (
                  <>
                    <div className="col-md-2"><label className="form-label">Vendor Name</label><input className="form-control form-control-sm bg-white" value={selectedVendor.vendorName} readOnly tabIndex="-1" /></div>
                    <div className="col-md-2"><label className="form-label">GSTIN</label><input className="form-control form-control-sm bg-white" value={selectedVendor.gstin || ""} readOnly tabIndex="-1" /></div>
                    <div className="col-md-2"><label className="form-label">Mobile No</label><input className="form-control form-control-sm bg-white" value={selectedVendor.mobile || ""} readOnly tabIndex="-1" /></div>
                    <div className="col-md-3"><label className="form-label">Address</label><input className="form-control form-control-sm bg-white" value={selectedVendor.address || ""} readOnly tabIndex="-1" /></div>
                  </>
                )}
              </div>
              <div className="row g-2 mt-0">
                <div className="col-md-2">
                  <label className="form-label">Bill No <span className="text-danger">*</span></label>
                  <input className={`form-control form-control-sm ${headerErrors.billNo ? "is-invalid" : ""}`} value={billNo}
                    onChange={(e) => { setBillNo(e.target.value); if (headerErrors.billNo) setHeaderErrors((p) => ({ ...p, billNo: undefined })); }} />
                  {headerErrors.billNo && <div className="mp-err">{headerErrors.billNo}</div>}
                </div>
                <div className="col-md-2">
                  <label className="form-label">Bill Date <span className="text-danger">*</span></label>
                  <input type="date" className={`form-control form-control-sm ${headerErrors.billDate ? "is-invalid" : ""}`} value={billDate}
                    onChange={(e) => { setBillDate(e.target.value); if (headerErrors.billDate) setHeaderErrors((p) => ({ ...p, billDate: undefined })); }} />
                  {headerErrors.billDate && <div className="mp-err">{headerErrors.billDate}</div>}
                </div>
                <div className="col-md-2">
                  <label className="form-label">Header CD</label>
                  <input className={`form-control form-control-sm ${headerErrors.headerCD ? "is-invalid" : ""}`} inputMode="decimal" value={headerCD}
                    onChange={(e) => { setHeaderCD(e.target.value); if (headerErrors.headerCD) setHeaderErrors((p) => ({ ...p, headerCD: undefined })); }} />
                  {headerErrors.headerCD && <div className="mp-err">{headerErrors.headerCD}</div>}
                </div>
                <div className="col-md-2"><label className="form-label">Transport</label><input className="form-control form-control-sm" value={transport} onChange={(e) => setTransport(e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label">Remarks</label><input className="form-control form-control-sm" value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
                <div className="col-md-2"><label className="form-label">Entry Date</label><input type="date" className="form-control form-control-sm" name="entryDate" value={currentItem.entryDate} onChange={handleItemChange} /></div>
              </div>
            </div>

            {/* ── Medicine Entry Section ── */}
            <div className="border p-2 mb-2 rounded bg-white section-box shadow-sm">
              {/* Row: Medicine + (wider) Composition + Category + Company */}
              <div className="row g-2 mb-1">
                <div className="col-md-3">
                  <label className="form-label fw-bold text-primary">Search Medicine <span className="text-danger">*</span></label>
                  <AsyncSelect
                    ref={medicineSelectRef} cacheOptions loadOptions={loadMedicineOptions} onChange={handleMedicineChange}
                    placeholder="Search medicine..." isClearable styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed"
                    className={itemErrors.medicineName ? "rs-invalid" : ""}
                    value={currentItem.medID ? { value: currentItem.medID, label: currentItem.medicineName } : null}
                  />
                  <Err f="medicineName" />
                </div>
                {/* ✅ Composition widened (col-5) */}
                <div className="col-md-5"><label className="form-label">Composition</label><input className="form-control form-control-sm bg-light" value={currentItem.medicinecomposition} readOnly tabIndex="-1" title={currentItem.medicinecomposition} /></div>
                <div className="col-md-2"><label className="form-label">Category</label><input className="form-control form-control-sm bg-light" value={currentItem.category} readOnly tabIndex="-1" /></div>
                <div className="col-md-2"><label className="form-label">Company</label><input className="form-control form-control-sm bg-light" value={currentItem.companyName} readOnly tabIndex="-1" /></div>
              </div>

              {/* Input Row 1: Batch / Pack / HSN / Mfg / Exp / Leaf / Piece / Free */}
              <div className="row g-2 mb-1">
                <div className="col-md-2">
                  <label className="form-label">Batch <span className="text-danger">*</span></label>
                  <input className={cls("form-control form-control-sm", "batchNo")} name="batchNo" value={currentItem?.batchNo || ""} onChange={handleItemChange} onBlur={(e) => fetchBatchDetails(e.target.value)} />
                  <Err f="batchNo" />
                </div>
                <div className="col-md-1"><label className="form-label">Pack</label><input className="form-control form-control-sm bg-light" value={currentItem.packSize} readOnly tabIndex="-1" /></div>
                <div className="col-md-1">
                  <label className="form-label">HSN <span className="text-danger">*</span></label>
                  <input className={cls("form-control form-control-sm", "hsnCode")} name="hsnCode" value={currentItem.hsnCode} onChange={handleItemChange} />
                  <Err f="hsnCode" />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Mfg <span className="text-danger">*</span></label>
                  <input type="date" className={cls("form-control form-control-sm", "mfgDate")} name="mfgDate" value={currentItem?.mfgDate} onChange={handleItemChange} />
                  <Err f="mfgDate" />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Exp <span className="text-danger">*</span></label>
                  <input type="date" className={cls("form-control form-control-sm", "expDate")} name="expDate" value={currentItem?.expDate} onChange={handleItemChange} />
                  <Err f="expDate" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Leaf</label>
                  <input className={cls("form-control form-control-sm", "leaf")} inputMode="numeric" name="leaf" value={currentItem.leaf} onChange={handleItemChange} />
                  <Err f="leaf" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Piece</label>
                  <input className={cls("form-control form-control-sm", "piece")} inputMode="numeric" name="piece" value={currentItem.piece} onChange={handleItemChange} />
                  <Err f="piece" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Free Leaf</label>
                  <input className={cls("form-control form-control-sm", "freeLeaf")} inputMode="numeric" name="freeLeaf" value={currentItem.freeLeaf} onChange={handleItemChange} />
                  <Err f="freeLeaf" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Free Pc</label>
                  <input className={cls("form-control form-control-sm", "freePiece")} inputMode="numeric" name="freePiece" value={currentItem.freePiece} onChange={handleItemChange} />
                  <Err f="freePiece" />
                </div>
              </div>
              {itemErrors.qty && <div className="mp-err mb-1">{itemErrors.qty}</div>}

              {/* Input Row 2: Rates / MRP / Sale / computed */}
              <div className="row g-2 mb-1 align-items-start">
                <div className="col-md-2">
                  <label className="form-label">Pur. Rate <span className="text-muted" style={{ fontSize: '10px' }}>(For {currentItem.packSize || 1} Pcs)</span> <span className="text-danger">*</span></label>
                  <input className={cls("form-control form-control-sm", "pRate")} inputMode="decimal" name="pRate" value={currentItem.pRate} onChange={handleItemChange} />
                  <Err f="pRate" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Dis %</label>
                  <input className={cls("form-control form-control-sm", "dis")} inputMode="decimal" name="dis" value={currentItem.dis} onChange={handleItemChange} />
                  <Err f="dis" />
                </div>
                <div className="col-md-1"><label className="form-label">GST %</label><input className="form-control form-control-sm bg-light" name="gstP" value={currentItem.gstP} readOnly tabIndex="-1" /></div>
                <div className="col-md-1">
                  <label className="form-label">MRP <span className="text-danger">*</span></label>
                  <input className={cls("form-control form-control-sm", "mrp")} inputMode="decimal" name="mrp" value={currentItem.mrp} onChange={handleItemChange} />
                  <Err f="mrp" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Sale Dis %</label>
                  <input className={cls("form-control form-control-sm", "saleDis")} inputMode="decimal" name="saleDis" value={currentItem.saleDis} onChange={handleItemChange} />
                  <Err f="saleDis" />
                </div>
                <div className="col-md-1">
                  <label className="form-label">Sale Rate</label>
                  <input className={cls("form-control form-control-sm", "sRate")} inputMode="decimal" name="sRate" value={currentItem.sRate} onChange={handleItemChange} />
                  <Err f="sRate" />
                </div>
                {currentItem.medID && (
                  <>
                    <div className="col-md-1"><label className="form-label">Tot Qty</label><input className="form-control form-control-sm bg-info bg-opacity-10" value={currentItem.totQtyInPcs} readOnly tabIndex="-1" /></div>
                    <div className="col-md-1"><label className="form-label">Free Qty</label><input className="form-control form-control-sm bg-warning bg-opacity-10" value={currentItem.freeQty} readOnly tabIndex="-1" /></div>
                    <div className="col-md-1"><label className="form-label">Taxable</label><input className="form-control form-control-sm" value={currentItem.taxable} readOnly tabIndex="-1" /></div>
                    <div className="col-md-1"><label className="form-label">GST Amt</label><input className="form-control form-control-sm" value={currentItem.gstAmt} readOnly tabIndex="-1" /></div>
                    <div className="col-md-1"><label className="form-label">Amount</label><input className="form-control form-control-sm bg-success text-white fw-bold" value={currentItem.totals} readOnly tabIndex="-1" /></div>
                  </>
                )}
              </div>

              {currentItem?.medID && (
                <div className="text-end">
                  <button type="button" className="btn btn-success btn-sm add-medicine-btn" onClick={handleAddItem}><i className="bi bi-plus-lg me-1"></i>Add Medicine</button>
                </div>
              )}
            </div>

            {/* ── Items Table ── */}
            <div className="table-responsive mb-2 mt-0" style={{ fontSize: "0.78rem", maxHeight: '300px' }}>
              <table className="custom-table table table-sm table-bordered table-hover align-middle text-center mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th><th>Med Name</th><th>Batch</th><th>Pack</th><th>L</th><th>P</th><th>FL</th><th>FP</th><th>Tot</th><th>Pur Rate</th><th>Dis%</th>
                    <th>GST%</th><th>Sale Rate</th><th>Mfg</th><th>Exp</th><th>Amount</th><th>Act</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? <tr><td colSpan="17" className="text-muted py-3">No items added.</td></tr> : items.map((it, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td><td className="text-start">{it.medicineName}</td><td>{it.batchNo}</td><td>{it.packSize}</td>
                      <td>{it.leaf}</td><td>{it.piece}</td><td>{it.freeLeaf}</td><td>{it.freePiece}</td><td>{it.qty}</td>
                      <td>{Number(it.pRate || 0).toFixed(2)}</td><td>{Number(it.dis || 0).toFixed(2)}</td><td>{it.gstP}</td>
                      <td>{Number(it.sRate || 0).toFixed(2)}</td><td>{it.mfgDate}</td><td>{it.expDate}</td>
                      <td className="fw-bold text-success">{Number(it.totals || 0).toFixed(2)}</td>
                      <td>
                        <button type="button" className="btn-icon btn-edit" onClick={() => handleEditRowToForm(idx)} tabIndex="-1"><i className="bi bi-pencil-square"></i></button>
                        <button type="button" className="btn-icon btn-delete ms-1" onClick={() => handleRemoveItem(idx)} tabIndex="-1"><i className="bi bi-trash"></i></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Summary ── */}
            {items.length > 0 && (
              <div className="mt-2 p-2 border rounded bg-info bg-opacity-10 d-flex justify-content-end gap-4 align-items-center">
                <div className="d-flex align-items-center gap-2"><span className="small fw-bold">Taxable:</span><span className="fw-bold text-dark">{`₹${totals.totalTaxable.toFixed(2)}`}</span></div>
                <div className="d-flex align-items-center gap-2"><span className="small fw-bold">GST:</span><span className="fw-bold text-dark">{`₹${totals.totalGst.toFixed(2)}`}</span></div>
                <div className="d-flex align-items-center gap-2"><span className="small fw-bold">Total:</span><span className="fs-6 fw-bold text-success">{`₹${totals.totalAmount.toFixed(2)}`}</span></div>
              </div>
            )}

            <div className="text-end mt-2">
              <button type="submit" className="btn btn-primary px-4 fw-bold" disabled={loading || items.length === 0 || !selectedVendor}>
                {loading ? "Saving..." : "Save Purchase"} <i className="bi bi-save ms-1"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .card-header { background: var(--theme-color, #0088cc); }
        .mp-compact .form-label { color: #495057; font-weight: 600; font-size: 0.68rem; margin-bottom: 1px; }
        /* ✅ Compact field heights */
        .mp-compact .form-control-sm, .mp-compact .form-select-sm {
          font-size: 0.78rem; height: 28px; min-height: 28px; padding: 1px 6px; border-color: #ced4da;
        }
        .mp-compact .form-control-sm:focus, .mp-compact .form-select-sm:focus {
          border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0, 136, 204, 0.25);
        }
        .mp-compact .row { --bs-gutter-y: 0.35rem; }
        .section-box { border-color: #e9ecef !important; }
        .btn-icon { border: none; background: transparent; padding: 0; }
        .btn-edit { color: #0d6efd; }
        .btn-delete { color: #dc3545; }
        .table-hover tbody tr:hover { background-color: #f1f3f5; }
        .alert-custom { padding: 6px 12px; border-radius: 4px; font-size: 0.82rem; font-weight: 500; }
        .alert-danger { background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb; }
        .alert-success { background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
        /* ✅ Validation styling */
        .mp-compact .is-invalid { border-color: #dc3545 !important; background-color: #fff6f6; }
        .mp-err { color: #dc3545; font-size: 0.66rem; line-height: 1.05; margin-top: 1px; }
        .rs-invalid .react-select__control { border-color: #dc3545 !important; }
      `}</style>
    </div>
  );
};

export default MedPurchase;
